import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Button,
  Space,
  message,
  Tag,
  Row,
  Col,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';
import { GlobalPropertiesService } from '../../services/api/globalPropertiesService';
import { GlobalPropertyDetailsModal } from '../GlobalProperties/GlobalPropertyDetailsModal';
import { ArtifactService } from '../../services/api/artifactService';
import { StateService } from '../../services/api/stateService';
import { ArtifactProperty, ArtifactItemState } from '../../types/artifactItems';
import { CompanyArtifactId, CArtifact } from '../../types/artifacts-branded';
import OptionCatalogManager from '../PrimeModel/OptionCatalogManager';
import { PropertyListTable } from './PropertyListTable';
import { PropertyDetailsPanel } from './PropertyDetailsPanel';
import { canUsePrimeArtifactFeatures } from '../AppShell/appNavAccess';
import { useAppNavAccess } from '../AppShell/useAppNavAccess';
import styles from './UnifiedPropertiesManager.module.css';

const { Option } = Select;
const { TextArea } = Input;

interface UnifiedPropertiesManagerProps {
  artifactId: CompanyArtifactId;
  usePrime: boolean;
  artifact?: CArtifact | null;
}

interface OptionCatalog {
  id: string;
  name: string;
  description?: string;
}

export const UnifiedPropertiesManager: React.FC<UnifiedPropertiesManagerProps> = ({
  artifactId,
  usePrime,
  artifact,
}) => {
  const [properties, setProperties] = useState<ArtifactProperty[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showCatalogManager, setShowCatalogManager] = useState(false);
  const [editingProperty, setEditingProperty] = useState<ArtifactProperty | null>(null);
  const [form] = Form.useForm();
  const [optionCatalogs, setOptionCatalogs] = useState<OptionCatalog[]>([]);
  const [artifactStates, setArtifactStates] = useState<ArtifactItemState[]>([]);
  const [artifactTypes, setArtifactTypes] = useState<Array<{ id: string; name: string }>>([]);
  const [showGlobalPropertyModal, setShowGlobalPropertyModal] = useState(false);
  const [selectedGlobalPropertyKey, setSelectedGlobalPropertyKey] = useState<string | null>(null);
  const [globalVisibilityProperties, setGlobalVisibilityProperties] = useState<Record<string, any>>({});
  const [selectedProperty, setSelectedProperty] = useState<ArtifactProperty | null>(null);
  const [searchText, setSearchText] = useState('');
  const navAccess = useAppNavAccess();
  const canApplyFromPrime = canUsePrimeArtifactFeatures(navAccess);
  const loadProperties = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const artifactProperties = await ArtifactService.getArtifactProperties(artifactId);
      setProperties(artifactProperties || []);
    } catch (err) {
      setError('Failed to load properties');
      console.error('Error loading properties:', err);
    } finally {
      setLoading(false);
    }
  }, [artifactId]);

  // Fetch option catalogs
  const fetchOptionCatalogs = useCallback(async () => {
    try {
      const response = await apiClient.get('/option-catalogs');
      const catalogs = response.data || [];
      setOptionCatalogs(catalogs);
    } catch (error) {
      console.error('Error fetching option catalogs:', error);
      setOptionCatalogs([]);
    }
  }, []);

  // Fetch artifact states
  const fetchArtifactStates = useCallback(async () => {
    try {
      const states = await StateService.getStatesByArtifact(artifactId);
      setArtifactStates(states || []);
    } catch (error) {
      console.error('Error fetching artifact states:', error);
      setArtifactStates([]);
    }
  }, [artifactId]);

  // Fetch artifact types
  const fetchArtifactTypes = useCallback(async () => {
    try {
      const response = await apiClient.get('/artifact-types');
      const types = Array.isArray(response.data) ? response.data : (response.data?.items || response.data?.data || []);
      setArtifactTypes(types);
    } catch (error) {
      console.error('Error fetching artifact types:', error);
      setArtifactTypes([]);
    }
  }, []);

  // Load global visibility properties
  // Note: company_id needs to be fetched from artifact_tree, so we'll skip for now
  // This can be enhanced later to fetch the tree and get company_id
  const loadGlobalVisibilityProperties = useCallback(async () => {
    // TODO: Fetch artifact_tree to get company_id, then load global visibility properties
    // For now, skip loading global visibility properties if company_id is not available
    // if (!artifact?.artifact_tree_id) return;
    // try {
    //   const tree = await ArtifactService.getTree(artifact.artifact_tree_id);
    //   if (tree?.company_id) {
    //     const response = await ArtifactService.getGlobalVisibilityProperties(tree.company_id);
    //     setGlobalVisibilityProperties(response.properties || {});
    //   }
    // } catch (error) {
    //   console.error('Error loading global visibility properties:', error);
    // }
  }, []);

  useEffect(() => {
    void loadProperties();
    void fetchOptionCatalogs();
    void fetchArtifactStates();
    void fetchArtifactTypes();
    void loadGlobalVisibilityProperties();
  }, [loadProperties, fetchOptionCatalogs, fetchArtifactStates, fetchArtifactTypes, loadGlobalVisibilityProperties]);

  const handleEditProperty = async (property: ArtifactProperty) => {
    setEditingProperty(property);

    // Extract created and updated states from state_mappings
    const createdStates = (property as any).state_mappings
      ?.filter((m: any) => m.mapping_type === 'created')
      .map((m: any) => m.state_name) || [];
    const updatedStates = (property as any).state_mappings
      ?.filter((m: any) => m.mapping_type === 'updated')
      .map((m: any) => m.state_name) || [];

    // Set form values
    form.setFieldsValue({
      property_key: property.property_key,
      property_type: property.property_type,
      description: property.description || '',
      is_required: property.is_required || false,
      input_source: property.input_source || undefined,
      property_scope: property.property_scope || undefined,
      global_option_catalog_ref: property.global_option_catalog_ref || undefined,
      default_value: property.default_value || '',
      input_label: property.input_label || '',
      input_type: property.input_type || undefined,
      is_child_reference: property.is_child_reference || false,
      created_in_states: createdStates,
      updated_in_states: updatedStates,
      validation_rules: property.validation_rules ? JSON.stringify(property.validation_rules, null, 2) : '',
    });

    fetchOptionCatalogs();
    setShowModal(true);
  };

  const handlePropertySelect = (property: ArtifactProperty) => {
    setSelectedProperty(property);
  };

  const handleViewGlobalProperty = async (propertyKey: string) => {
    setSelectedGlobalPropertyKey(propertyKey);
    try {
      await GlobalPropertiesService.getPropertyDefinition(propertyKey);
      setShowGlobalPropertyModal(true);
    } catch (error) {
      message.warning(`Global property definition for "${propertyKey}" not found`);
    }
  };

  const handleSaveProperty = async () => {
    try {
      const values = await form.validateFields();
      console.log('Form values:', values);

      // Parse validation rules if provided
      if (values.validation_rules) {
        try {
          values.validation_rules = JSON.parse(values.validation_rules);
        } catch (e) {
          message.error('Invalid JSON in validation rules');
          return;
        }
      }

      // Extract state mappings
      const stateMappings: Array<{ state_name: string; mapping_type: 'created' | 'updated' }> = [];

      if (values.created_in_states && values.created_in_states.length > 0) {
        values.created_in_states.forEach((stateName: string) => {
          stateMappings.push({ state_name: stateName, mapping_type: 'created' });
        });
      }

      if (values.updated_in_states && values.updated_in_states.length > 0) {
        values.updated_in_states.forEach((stateName: string) => {
          stateMappings.push({ state_name: stateName, mapping_type: 'updated' });
        });
      }

      // Extract artifact references (simplified for now - can be enhanced later)
      const artifactReferences: Array<{
        referenced_artifact_type_id?: string;
        artifact_id?: string;
        reference_type?: 'uuid' | 'transformed';
        source_property_key?: string;
        transformation_logic?: any;
        relationship_type?: string;
        notes?: string;
      }> = [];

      // For now, skip artifact references - they can be added later if needed
      // This keeps the initial implementation simpler

      // Remove the state mapping arrays from values
      delete values.created_in_states;
      delete values.updated_in_states;

      // Prepare request body
      const requestBody = {
        ...values,
        state_mappings: stateMappings,
        referenced_artifacts: artifactReferences.length > 0 ? artifactReferences : undefined,
      };

      if (editingProperty) {
        // Update existing property - use artifact-scoped path
        console.log('Updating property with data:', requestBody);
        await apiClient.put(`/artifacts/${artifactId}/properties/${editingProperty.id}`, requestBody);
        message.success('Property updated successfully');
      } else {
        // Create new property - use artifact-scoped path
        const createBody = {
          ...requestBody,
        };
        console.log('Creating property with data:', createBody);
        await apiClient.post(`/artifacts/${artifactId}/properties`, createBody);
        message.success('Property created successfully');
      }

      setShowModal(false);
      const editedPropertyId = editingProperty?.id;
      setEditingProperty(null);
      form.resetFields();
      await loadProperties();
      // Refresh selected property if it was the one being edited
      if (selectedProperty && editedPropertyId && selectedProperty.id === editedPropertyId) {
        const updatedProperties = await ArtifactService.getArtifactProperties(artifactId);
        const updatedProperty = updatedProperties?.find(p => p.id === selectedProperty.id);
        if (updatedProperty) {
          setSelectedProperty(updatedProperty);
        }
      }
    } catch (error: any) {
      console.error('Save property error:', error);

      // Check if it's a validation error from Ant Design
      if (error.errorFields && error.errorFields.length > 0) {
        const errorFields = error.errorFields.map((ef: any) => `${ef.name}: ${ef.errors.join(', ')}`).join('; ');
        console.error('Validation errors:', errorFields);
        message.error('Validation failed: ' + errorFields);
      } else {
        message.error(`Failed to ${editingProperty ? 'update' : 'create'} property: ${error.response?.data?.detail || error.message || 'Unknown error'}`);
      }
    }
  };

  const handleApplyFromPrime = async () => {
    if (!artifact?.prime_artifact_id) {
      setError('This artifact is not linked to a prime artifact. Cannot apply from prime.');
      return;
    }

    const confirmed = window.confirm(
      'This will apply all properties from the linked prime artifact to this company artifact.\n\nAre you sure you want to continue?'
    );

    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.post(
        `/artifacts/${artifactId}/apply-prime-properties`,
        null,
        { params: { prime_artifact_id: artifact.prime_artifact_id } }
      );
      
      // Refresh properties after application
      await loadProperties();
      
      alert(`Prime properties applied successfully! Created ${response.data.properties_created || 0} properties.`);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || 'Failed to apply prime properties';
      setError(errorMessage);
      console.error('Error applying prime properties:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && properties.length === 0) {
    return <div className={styles.unifiedPropertiesManager}>Loading properties...</div>;
  }

  return (
    <div className={styles.unifiedPropertiesManager}>
      <div className={styles.propertiesHeader}>
        <h2>Properties ({usePrime ? 'Creating Prime' : 'Creating Regular'})</h2>
        <div className={styles.propertiesActions}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingProperty(null);
              form.resetFields();
              form.setFieldsValue({
                property_key: '',
                property_type: undefined,
                description: '',
                is_required: false,
                input_source: undefined,
                property_scope: undefined,
                global_option_catalog_ref: undefined,
                default_value: '',
                created_in_states: [],
                updated_in_states: [],
              });
              fetchOptionCatalogs();
              setShowModal(true);
            }}
            style={{ marginRight: 8 }}
          >
            Add Property
          </Button>
          {artifact?.prime_artifact_id && (
            <button
              onClick={handleApplyFromPrime}
              className={styles.applyFromPrimeButton}
              disabled={loading || !canApplyFromPrime}
              title={
                canApplyFromPrime
                  ? 'Apply all properties from linked prime artifact'
                  : 'Requires prime_artifacts subscription or platform admin access'
              }
            >
              Apply from Prime
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className={styles.errorMessage}>
          {error}
        </div>
      )}

      <div className={styles.propertiesContent}>
        {properties.length === 0 ? (
          <div className={styles.noProperties}>
            <p>No properties found.</p>
            {artifact?.prime_artifact_id && (
              <p className={styles.hint}>Click "Apply from Prime" to import properties from the linked prime artifact.</p>
            )}
          </div>
        ) : (
          <Row gutter={16} style={{ height: '100%' }}>
            {/* Left Pane - Properties List */}
            <Col xs={24} lg={12} xl={12}>
              <PropertyListTable
                properties={properties}
                loading={loading}
                searchText={searchText}
                onSearchChange={setSearchText}
                onRefresh={loadProperties}
                onRowClick={handlePropertySelect}
                selectedPropertyId={selectedProperty?.id || null}
                globalVisibilityProperties={globalVisibilityProperties}
                onViewGlobalProperty={handleViewGlobalProperty}
              />
            </Col>

            {/* Right Pane - Property Details */}
            <Col xs={24} lg={12} xl={12}>
              <PropertyDetailsPanel
                property={selectedProperty}
                loading={loading}
                artifactStates={artifactStates}
                onEdit={handleEditProperty}
                onViewGlobalProperty={handleViewGlobalProperty}
                globalVisibilityProperties={globalVisibilityProperties}
              />
            </Col>
          </Row>
        )}
      </div>

      {/* Add/Edit Property Modal */}
      <Modal
        title={editingProperty ? 'Edit Property' : 'Add Property'}
        open={showModal}
        onOk={handleSaveProperty}
        onCancel={() => {
          setShowModal(false);
          setEditingProperty(null);
          form.resetFields();
        }}
        width={800}
        okText="Save"
        cancelText="Cancel"
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            label="Property Key"
            name="property_key"
            rules={[{ required: true, message: 'Please enter property key' }]}
          >
            <Input placeholder="e.g., has_business_plan" disabled={!!editingProperty} />
          </Form.Item>

          <Form.Item
            label="Property Type"
            name="property_type"
            rules={[{ required: true, message: 'Please select property type' }]}
          >
            <Select placeholder="Select property type">
              <Option value="string">String</Option>
              <Option value="number">Number</Option>
              <Option value="boolean">Boolean</Option>
              <Option value="date">Date</Option>
              <Option value="date_range">Date Range</Option>
              <Option value="currency">Currency</Option>
              <Option value="duration">Duration</Option>
              <Option value="url">URL</Option>
              <Option value="uuid">UUID</Option>
              <Option value="uuid[]">Array of UUIDs</Option>
              <Option value="list">List (from Option Catalog)</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Input Source"
            name="input_source"
            tooltip="How this property value is obtained"
          >
            <Select placeholder="Select input source">
              <Option value="user_input">User Input</Option>
              <Option value="system_generated">System Generated</Option>
              <Option value="referenced_artifact">Referenced Artifact (UUID)</Option>
              <Option value="transformed_reference">Transformed Reference (Calculated)</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Property Scope"
            name="property_scope"
            tooltip="Determines whether this property applies to the artifact level or artifactItem level"
          >
            <Select placeholder="Select scope (optional)">
              <Option value="artifact">Artifact</Option>
              <Option value="artifact_item">Artifact Item</Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.input_source !== currentValues.input_source
            }
          >
            {({ getFieldValue }) => {
              const inputSource = getFieldValue('input_source');
              const isReference = inputSource === 'referenced_artifact' || inputSource === 'transformed_reference';

              if (!isReference) {
                return null;
              }

              return (
                <Form.Item
                  label="Child Reference"
                  name="is_child_reference"
                  valuePropName="checked"
                  tooltip="Child references require creation and the workflow waits for the child artifact to be created"
                >
                  <Switch />
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.input_source !== currentValues.input_source ||
              prevValues.property_type !== currentValues.property_type
            }
          >
            {({ getFieldValue }) => {
              const inputSource = getFieldValue('input_source');
              const propertyType = getFieldValue('property_type');

              if (inputSource !== 'user_input') {
                return null;
              }

              return (
                <>
                  <Form.Item
                    label="Input Label"
                    name="input_label"
                    tooltip="User-facing label for this input field"
                  >
                    <Input placeholder="Label for user input (e.g., 'Reporting Period Type')" />
                  </Form.Item>

                  <Form.Item
                    label="Input Type"
                    name="input_type"
                    tooltip="Specific input type for formatting"
                  >
                    <Select placeholder="Select input type">
                      <Option value="text">Text</Option>
                      <Option value="number">Number</Option>
                      <Option value="boolean">Boolean (Checkbox)</Option>
                      <Option value="date">Date</Option>
                      <Option value="date_range">Date Range</Option>
                      <Option value="currency">Currency</Option>
                      <Option value="duration">Duration</Option>
                      <Option value="url">URL</Option>
                      <Option value="select">Select (Dropdown)</Option>
                      <Option value="multi_select">Multi-Select</Option>
                      <Option value="textarea">Textarea</Option>
                    </Select>
                  </Form.Item>
                </>
              );
            }}
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
          >
            <TextArea rows={2} placeholder="Describe this property" />
          </Form.Item>

          <Form.Item
            label="Required"
            name="is_required"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            label="Global Option Catalog"
            name="global_option_catalog_ref"
            tooltip="Required for 'List' type. Select or create a catalog for list options"
            dependencies={['property_type']}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (getFieldValue('property_type') === 'list' && !value) {
                    return Promise.reject(new Error('Option catalog is required for List type'));
                  }
                  return Promise.resolve();
                }
              })
            ]}
          >
            <Select
              placeholder={optionCatalogs.length === 0 ? 'No catalogs available. Click "Manage Catalogs" to create one.' : 'Select option catalog'}
              allowClear
              showSearch
              loading={optionCatalogs.length === 0}
              filterOption={(input, option) => {
                const label = typeof option?.label === 'string'
                  ? option.label
                  : typeof option?.children === 'string'
                    ? option.children
                    : String(option?.label || option?.children || '');
                return label.toLowerCase().includes(input.toLowerCase());
              }}
              notFoundContent={optionCatalogs.length === 0 ? 'No catalogs available. Click "Manage Catalogs" to create one.' : 'No catalogs found'}
              onOpenChange={(open) => {
                if (open) {
                  fetchOptionCatalogs();
                }
              }}
              popupRender={(menu) => (
                <>
                  {menu}
                  <Button
                    type="link"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setShowCatalogManager(true);
                    }}
                    style={{ width: '100%', textAlign: 'center' }}
                  >
                    Manage Catalogs
                  </Button>
                </>
              )}
            >
              {optionCatalogs.length > 0 ? (
                optionCatalogs.map(catalog => (
                  <Option key={catalog.id} value={catalog.id}>
                    {catalog.name}
                  </Option>
                ))
              ) : (
                <Option disabled value="no-catalogs">
                  No catalogs available
                </Option>
              )}
            </Select>
          </Form.Item>

          <Form.Item
            label="Default Value"
            name="default_value"
          >
            <Input placeholder="Default value for this property" />
          </Form.Item>

          <Form.Item
            label="Created In States"
            name="created_in_states"
            tooltip="Select states where this property is created (initialized for the first time)"
          >
            <Select
              mode="multiple"
              placeholder="Select states where property is created"
              allowClear
            >
              {artifactStates.map(state => (
                <Option key={state.id} value={state.name}>
                  {state.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Updated In States"
            name="updated_in_states"
            tooltip="Select states where this property value is updated"
          >
            <Select
              mode="multiple"
              placeholder="Select states where property is updated"
              allowClear
            >
              {artifactStates.map(state => (
                <Option key={state.id} value={state.name}>
                  {state.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Validation Rules (JSON)"
            name="validation_rules"
            tooltip="JSON object with validation rules (e.g., min, max, pattern, etc.)"
          >
            <TextArea
              rows={4}
              placeholder='{"min": 0, "max": 100}'
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Option Catalog Manager Modal */}
      <Modal
        title="Manage Option Catalogs"
        open={showCatalogManager}
        onCancel={() => setShowCatalogManager(false)}
        footer={null}
        width={1000}
      >
        <OptionCatalogManager
          visible={showCatalogManager}
          onClose={() => {
            setShowCatalogManager(false);
            fetchOptionCatalogs();
          }}
          onCatalogSelected={(catalogId) => {
            fetchOptionCatalogs().then(() => {
              form.setFieldsValue({ global_option_catalog_ref: catalogId });
              setShowCatalogManager(false);
            });
          }}
        />
      </Modal>

      {showGlobalPropertyModal && selectedGlobalPropertyKey && (
        <GlobalPropertyDetailsModal
          propertyKey={selectedGlobalPropertyKey}
          visible={showGlobalPropertyModal}
          onClose={() => {
            setShowGlobalPropertyModal(false);
            setSelectedGlobalPropertyKey(null);
          }}
        />
      )}
    </div>
  );
};

